import { useEffect, useRef, useState } from 'react';
import useEventListener from '../../hooks/useEventListener';
import { editingTicket, navigateAwayFromTicket, visitingTicket } from './ticketActivityHelper';

const EDITING_TICKET_REQUEST_INTERVAL = 60000;
enum TicketLifecycleEventName {
    /**
     * Ticket's tab is opened. This event is fired when either a new tab is created or an existed tab is opened.
     */
    TicketOpened = 'TicketOpened',
    /**
     * Ticket form is edited in the UI (not posted to backend)
     */
    TicketEdited = 'TicketEdited',
    /**
     * Ticket form is reset after editing. This event is usually fired after one or more TicketEdited event however,
     * this is not guaranteed due to page refreshes and loads from local storage.
     */
    TicketReset = 'TicketReset',
    /**
     * Ticket form is submitted successfully (HTTP response is success)
     * This event is only fired when a new ticket is created. Won't fire when a ticket is updated.
     */
    TicketCreated = 'TicketCreated',
    /**
     * Ticket form is submitted successfully (HTTP response is success)
     * This event is only fired when an existing ticket is updated. Won't fire when a ticket is created.
     */
    TicketUpdated = 'TicketUpdated',
    /**
     * Ticket tab is closed.
     * Note that this event has nothing to do with ticket status.
     */
    TicketClosed = 'TicketClosed'
}

function fireTicketLifecycleEvent(eventName: TicketLifecycleEventName, ticketKey: string): void {
    // console.debug(LOG_LABEL, 'firing', eventName, ticketKey);
    window.dispatchEvent(new CustomEvent(eventName, { detail: ticketKey }));
}
export default function TicketActivityListener({ tabKey }: { tabKey: string }) {
    const [editingStarted, setEditingStarted] = useState(false);
    const ticketKeyRef = useRef<string>();

    useEffect(() => {
        tabKey?.startsWith('TICKET') && visitingTicket(tabKey);
        return () => tabKey?.startsWith('TICKET') && navigateAwayFromTicket(tabKey);
    }, [tabKey]);

    // fired from EditTicketForm
    useEventListener(TicketLifecycleEventName.TicketEdited, (e: WindowEventMap['TicketEdited']) => {
        if (e?.detail === tabKey && tabKey?.startsWith('TICKET')) {
            ticketKeyRef.current = e?.detail;
            setEditingStarted(true);
        }
    });

    useEffect(() => {
        editingStarted && editingTicket(ticketKeyRef.current);
        const editingTicketRequest =
            editingStarted &&
            setInterval(() => {
                editingTicket(ticketKeyRef.current);
            }, EDITING_TICKET_REQUEST_INTERVAL);

        return () => clearInterval(editingTicketRequest);
    }, [editingStarted]);

    return null;
}

export { fireTicketLifecycleEvent, TicketLifecycleEventName };
